import React from "react";
import { Box, GridLegacy as Grid, Typography, IconButton, CircularProgress, Checkbox } from "@mui/material";
import { Image as ImageIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { ComplaintImage } from "../BookingComplaint";
import { ComplaintImageBox } from "./ComplaintImageBox";
import { theme } from "../../../../theme";

interface ImageGridProps {
    images: ComplaintImage[];
    selectedImages?: ComplaintImage[];
    uploadingImages?: Set<string>;
    deletingImageId?: string | null;
    onImageSelect?: (image: ComplaintImage) => void;
    onImageDelete?: (image: ComplaintImage) => void;
    showSelection?: boolean;
    showDelete?: boolean;
    emptyStateMessage?: string;
    emptyStateIcon?: React.ReactNode;
    gridSize?: "small" | "medium" | "large";
    imageFit?: "contain" | "cover" | "fill" | "scale-down";
}

export const ImageGrid: React.FC<ImageGridProps> = ({
    images,
    selectedImages = [],
    uploadingImages = new Set(),
    deletingImageId = null,
    onImageSelect,
    onImageDelete,
    showSelection = true,
    showDelete = true,
    emptyStateMessage = "No images available",
    emptyStateIcon,
    gridSize = "medium",
    imageFit = "scale-down" // Using scale-down as default to avoid upscaling a low res image
}) => {
    const getGridItemSize = () => {
        switch (gridSize) {
            case "small": return { xs: 6, sm: 4, md: 3 };
            case "large": return { xs: 12, sm: 6, md: 4 };
            default: return { xs: 12, sm: 6 };
        }
    };

    const getImageHeight = () => {
        switch (gridSize) {
            case "small": return 120;
            case "large": return 250;
            default: return 200;
        }
    };

    if (images.length === 0) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
                color: "text.secondary"
            }}>
                {emptyStateIcon || <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />}
                <Typography variant="body2">
                    {emptyStateMessage}
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            {images.map((image, index) => (
                <Grid item {...getGridItemSize()} key={image.clientId || `image-${index}`}>
                    <ComplaintImageBox
                        image={image}
                        imageHeight={getImageHeight()}
                        imageFit={imageFit}
                        onClick={uploadingImages.has(image.clientId) ? undefined : () => onImageSelect?.(image)}
                        style={{ opacity: uploadingImages.has(image.clientId) ? 0.7 : 1 }}
                    >
                        {/* Selection Checkbox */}
                        {showSelection && !uploadingImages.has(image.clientId) && onImageSelect && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    zIndex: 1
                                }}
                            >
                                <Checkbox
                                    checked={selectedImages.some(img => img.clientId === image.clientId)}
                                    onChange={() => onImageSelect(image)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        bgcolor: "rgba(255, 255, 255, 0.9)",
                                        borderRadius: "50%",
                                        padding: "4px",
                                        "&:hover": {
                                            bgcolor: "rgba(255, 255, 255, 1)",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            fontSize: 20
                                        },
                                        color: theme.palette.brand.main,
                                        "&.Mui-checked": {
                                            color: theme.palette.brand.main,
                                        }
                                    }}
                                />
                            </Box>
                        )}
                        {/* Delete Button */}
                        {showDelete && !uploadingImages.has(image.clientId) && onImageDelete && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    zIndex: 1
                                }}
                            >
                                <IconButton
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(255, 255, 255, 0.9)",
                                        "&:hover": { bgcolor: "rgba(244, 67, 54, 0.1)" }
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onImageDelete(image);
                                    }}
                                    disabled={deletingImageId === image.clientId}
                                >
                                    {deletingImageId === image.clientId ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <DeleteIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Box>
                        )}
                    </ComplaintImageBox>
                </Grid>
            ))}
        </Grid>
    );
}; 